/**
 * useLicenseStatus Hook (DC-1500)
 *
 * Fetches license status from GET /api/config/license and provides
 * reactive state for the UI to display banners, gate modules, etc.
 *
 * @module client/hooks/useLicenseStatus
 */

import { useState, useEffect, useCallback } from 'react';

// ============================================================================
// TYPES
// ============================================================================

export type LicenseStatus =
  | 'valid'
  | 'warning'
  | 'read_only'
  | 'locked_out'
  | 'missing'
  | 'invalid'
  | 'loading'
  | 'error';

export interface LicenseInfo {
  status: LicenseStatus;
  customer_name: string | null;
  edition: 'professional' | 'enterprise' | null;
  activated_modules: string[];
  activated_integrations: string[];
  max_users: number;
  days_remaining: number | null;
  expires_at: string | null;
}

const INITIAL_STATE: LicenseInfo = {
  status: 'loading',
  customer_name: null,
  edition: null,
  activated_modules: [],
  activated_integrations: [],
  max_users: 0,
  days_remaining: null,
  expires_at: null,
};

// ============================================================================
// HOOK
// ============================================================================

/**
 * Fetch and cache the license status. Re-fetches every 30 minutes.
 */
export function useLicenseStatus(): LicenseInfo & { refresh: () => void } {
  const [info, setInfo] = useState<LicenseInfo>(INITIAL_STATE);

  const fetchLicense = useCallback(async () => {
    try {
      const res = await fetch('/api/config/license');
      if (!res.ok) {
        setInfo(prev => ({ ...prev, status: 'error' }));
        return;
      }
      const data = await res.json();
      setInfo({
        status: data.status ?? 'error',
        customer_name: data.customer_name ?? null,
        edition: data.edition ?? null,
        activated_modules: data.activated_modules ?? [],
        activated_integrations: data.activated_integrations ?? [],
        max_users: data.max_users ?? 0,
        days_remaining: data.days_remaining ?? null,
        expires_at: data.expires_at ?? null,
      });
    } catch {
      setInfo(prev => ({ ...prev, status: 'error' }));
    }
  }, []);

  useEffect(() => {
    void fetchLicense();

    // Re-fetch every 30 minutes
    const interval = setInterval(() => void fetchLicense(), 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchLicense]);

  return { ...info, refresh: fetchLicense };
}

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Check if a module is activated in the license.
 */
export function isModuleLicensed(info: LicenseInfo, moduleName: string): boolean {
  return info.activated_modules.includes(moduleName);
}

/**
 * Check if an integration is activated in the license.
 */
export function isIntegrationLicensed(info: LicenseInfo, integrationId: string): boolean {
  return info.activated_integrations.includes(integrationId);
}
